// apps/web/src/lib/zxcvbn-lite.ts
//
// Plan 04-12b — tiny heuristic password-strength estimator.
//
// CONTEXT D-04 (client-side): show a zxcvbn-style strength indicator to nudge
// users away from weak passwords. The server (Better-Auth) accepts at
// minPasswordLength=8; the client never blocks submission based on this score
// — it is purely an educational UX nudge.
//
// CONTEXT D-04 prescribes min-score 3 as the visual nudge. We don't enforce
// that on submit (server is the authority).
//
// WHY NOT zxcvbn-ts (3.0.5): Plan 12b doesn't pin it in STACK.md, and the
// real zxcvbn dictionary brings ~400 KB of language-dictionary bundle weight
// for a UX nudge. This lite version is ~50 lines and gives the same 0-4
// score buckets via a simple length + character-class heuristic:
//
//   - length < 6                              → 0 ("very weak")
//   - length < 8                              → 1 ("weak")
//   - length < 10                             → 2 ("fair")
//   - length >= 10 with 2 char classes        → 2 ("fair")
//   - length >= 10 with 3 char classes        → 3 ("good")
//   - length >= 12 with 3+ char classes       → 4 ("strong")
//
// Character classes = {lower, upper, digit, special}.
//
// FUTURE: if dictionary-based scoring matters (penalize "password123"),
// swap in zxcvbn-ts behind the same { score, label, fraction } contract.

export interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4;
  label: "very weak" | "weak" | "fair" | "good" | "strong";
  fraction: number; // 0.0 - 1.0 — convenient for a progress bar fill
}

const LABELS: readonly PasswordStrength["label"][] = [
  "very weak",
  "weak",
  "fair",
  "good",
  "strong",
] as const;

function countCharClasses(password: string): number {
  let classes = 0;
  if (/[a-z]/.test(password)) classes += 1;
  if (/[A-Z]/.test(password)) classes += 1;
  if (/[0-9]/.test(password)) classes += 1;
  if (/[^a-zA-Z0-9]/.test(password)) classes += 1;
  return classes;
}

/**
 * Score a password 0-4. Pure function — same input always produces the same
 * output. NEVER use this as a server-side gate; it's a UX nudge only.
 */
export function scorePassword(password: string): PasswordStrength {
  const len = password.length;
  if (len === 0) {
    return { score: 0, label: "very weak", fraction: 0 };
  }
  const classes = countCharClasses(password);

  let score: 0 | 1 | 2 | 3 | 4;
  if (len < 6) {
    score = 0;
  } else if (len < 8) {
    score = 1;
  } else if (len < 10) {
    score = classes >= 3 ? 2 : 1;
  } else if (len < 12) {
    if (classes >= 3) score = 3;
    else if (classes >= 2) score = 2;
    else score = 1;
  } else {
    // len >= 12
    if (classes >= 3) score = 4;
    else if (classes >= 2) score = 3;
    else score = 2;
  }

  return {
    score,
    label: LABELS[score]!,
    fraction: score / 4,
  };
}
